import { getWorkflow } from "../services/regulationsService";

/** Read PAT association id when the workflows list includes it. */
export function extractProcessAssociationIdFromListItem(
  item: Record<string, unknown>,
): string {
  const v = item.processAssociationId ?? item.process_association_id;
  return v != null ? String(v).trim() : "";
}

/** Workflow uuid for GET /manual-assessment/workflows/{id} (detail includes processAssociationId). */
export function getWorkflowUuidFromListItem(
  item: Record<string, unknown>,
): string | null {
  for (const key of ["uuid", "workflow_id"] as const) {
    const v = item[key];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  if (typeof item.id === "string" && item.id.includes(":")) {
    return item.id.trim();
  }
  return null;
}

/**
 * List endpoint often omits process_association_id; workflow detail has it.
 * Fetches detail by uuid (cached per page) when the list row is missing the id.
 */
export async function enrichManualWorkflowApiItemsWithProcessAssociationId<
  T extends Record<string, unknown>,
>(items: T[]): Promise<T[]> {
  const cache = new Map<string, string>();

  return Promise.all(
    items.map(async (item) => {
      const existing = extractProcessAssociationIdFromListItem(item);
      if (existing) return item;

      const workflowUuid = getWorkflowUuidFromListItem(item);
      if (!workflowUuid) return item;

      if (!cache.has(workflowUuid)) {
        let resolved = "";
        try {
          const res = await getWorkflow(workflowUuid);
          const detail = res.data as Record<string, unknown> | null;
          resolved = String(
            detail?.processAssociationId ?? detail?.process_association_id ?? "",
          ).trim();
        } catch {
          resolved = "";
        }
        cache.set(workflowUuid, resolved);
      }

      const associationId = cache.get(workflowUuid) ?? "";
      if (!associationId) return item;

      return {
        ...item,
        processAssociationId: associationId,
        process_association_id: associationId,
      };
    }),
  );
}
